import { useEffect, useState } from "react";
import { AdminLayout } from "../components/AdminLayout";
import axios from "axios";
import { getAuthHeader } from "../context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Trash2, Edit, TrendingUp, X } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

export default function UpsaleDiscountsPage() {
  const [upsaleDiscounts, setUpsaleDiscounts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingDiscount, setEditingDiscount] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    discount_value: "",
    combination_items: [],
    is_active: true,
  });
  const [creditSlabs, setCreditSlabs] = useState([]);
  const [creditModalOpen, setCreditModalOpen] = useState(false);
  const [editingSlab, setEditingSlab] = useState(null);

  const [creditForm, setCreditForm] = useState({
    min_value: "",
    max_value: "",
    credit_percent: "",
  });

  useEffect(() => {
    fetchUpsaleDiscounts();
    fetchCategories();
    fetchMenuItems();
    fetchCreditSlabs();
  }, []);

  const fetchCreditSlabs = async () => {
    try {
      const response = await axios.get(`${API}/credit-slabs`, {
        headers: getAuthHeader(),
      });
      setCreditSlabs(response.data);
    } catch (error) {
      console.error("Error fetching credit slabs:", error);
      toast.error("Failed to load credit slabs");
    }
  };

  const handleSaveSlab = async () => {
    if (
      !creditForm.min_value ||
      !creditForm.max_value ||
      !creditForm.credit_percent
    ) {
      toast.error("Please fill all fields");
      return;
    }

    const payload = {
      min_value: Number(creditForm.min_value),
      max_value: Number(creditForm.max_value),
      credit_percent: Number(creditForm.credit_percent),
    };

    try {
      if (editingSlab) {
        // Update existing slab
        await axios.put(`${API}/credit-slabs/${editingSlab}`, payload, {
          headers: getAuthHeader(),
        });
        toast.success("Credit slab updated");
      } else {
        // Create new slab
        await axios.post(`${API}/credit-slabs`, payload, {
          headers: getAuthHeader(),
        });
        toast.success("Credit slab created");
      }

      setCreditModalOpen(false);
      fetchCreditSlabs();
    } catch (error) {
      console.error("Error saving slab:", error);
      toast.error("Failed to save credit slab");
    }
  };
  const handleDeleteSlab = async (id) => {
    if (!window.confirm("Are you sure you want to delete this credit slab?"))
      return;

    try {
      await axios.delete(`${API}/credit-slabs/${id}`, {
        headers: getAuthHeader(),
      });
      toast.success("Credit slab deleted");
      fetchCreditSlabs();
    } catch (error) {
      console.error("Error deleting slab:", error);
      toast.error("Failed to delete credit slab");
    }
  };
  const handleEditSlab = (slab) => {
    setCreditForm({
      min_value: slab.min_value,
      max_value: slab.max_value,
      credit_percent: slab.credit_percent,
    });
    setEditingSlab(slab.id);
    setCreditModalOpen(true);
  };

  const fetchUpsaleDiscounts = async () => {
    try {
      const response = await axios.get(`${API}/upsale-discounts`, {
        headers: getAuthHeader(),
      });
      setUpsaleDiscounts(response.data);
    } catch (error) {
      console.error("Error fetching upsale discounts:", error);
      toast.error("Failed to load upsale discounts");
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`, {
        headers: getAuthHeader(),
      });
      setCategories(response.data);
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  const fetchMenuItems = async () => {
    try {
      const response = await axios.get(`${API}/menu/items`, {
        headers: getAuthHeader(),
      });
      setMenuItems(response.data);
    } catch (error) {
      console.error("Error fetching menu items:", error);
    }
  };

  const handleAddDiscount = () => {
    setFormData({
      name: "",
      description: "",
      discount_value: "",
      combination_items: [],
      is_active: true,
    });
    setEditingDiscount(null);
    setModalOpen(true);
  };

  const handleEditDiscount = (discount) => {
    setFormData({
      name: discount.name,
      description: discount.description,
      discount_value: discount.discount_value,
      combination_items: discount.combination_items || [],
      is_active: discount.is_active,
    });
    setEditingDiscount(discount.id);
    setModalOpen(true);
  };

  const addCombinationItem = (type) => {
    setFormData({
      ...formData,
      combination_items: [
        ...formData.combination_items,
        { type, id: "", name: "", is_upsale_target: false },
      ],
    });
  };

  const updateCombinationItem = (index, field, value) => {
    const updated = [...formData.combination_items];
    updated[index][field] = value;

    // Auto-fill name when id is selected
    if (field === "id") {
      if (updated[index].type === "category") {
        const category = categories.find((c) => c.id === value);
        if (category) updated[index].name = category.name;
      } else if (updated[index].type === "item") {
        const item = menuItems.find((i) => i.id === value);
        if (item) updated[index].name = item.name;
      }
    }

    setFormData({ ...formData, combination_items: updated });
  };

  const removeCombinationItem = (index) => {
    setFormData({
      ...formData,
      combination_items: formData.combination_items.filter(
        (_, i) => i !== index
      ),
    });
  };

  const handleSave = async () => {
    if (!formData.name || !formData.description || !formData.discount_value) {
      toast.error("Please fill in all required fields");
      return;
    }

    if (formData.combination_items.length < 2) {
      toast.error("Please add at least 2 items to the combination");
      return;
    }

    const hasUpsaleTarget = formData.combination_items.some(
      (item) => item.is_upsale_target
    );
    if (!hasUpsaleTarget) {
      toast.error(
        "Please mark at least one item as the upsale target (the item that gets the discount)"
      );
      return;
    }

    try {
      const payload = {
        ...formData,
        discount_value: parseFloat(formData.discount_value),
      };

      if (editingDiscount) {
        await axios.put(`${API}/upsale-discounts/${editingDiscount}`, payload, {
          headers: getAuthHeader(),
        });
        toast.success("Upsale discount updated successfully");
      } else {
        await axios.post(`${API}/upsale-discounts`, payload, {
          headers: getAuthHeader(),
        });
        toast.success("Upsale discount created successfully");
      }

      setModalOpen(false);
      fetchUpsaleDiscounts();
    } catch (error) {
      console.error("Error saving upsale discount:", error);
      toast.error("Failed to save upsale discount");
    }
  };

  const handleDelete = async (id) => {
    if (
      !window.confirm("Are you sure you want to delete this upsale discount?")
    )
      return;

    try {
      await axios.delete(`${API}/upsale-discounts/${id}`, {
        headers: getAuthHeader(),
      });
      toast.success("Upsale discount deleted");
      fetchUpsaleDiscounts();
    } catch (error) {
      console.error("Error deleting upsale discount:", error);
      toast.error("Failed to delete upsale discount");
    }
  };

  const toggleActive = async (discount) => {
    try {
      await axios.put(
        `${API}/upsale-discounts/${discount.id}`,
        {
          ...discount,
          is_active: !discount.is_active,
        },
        { headers: getAuthHeader() }
      );
      toast.success(
        `Upsale discount ${!discount.is_active ? "activated" : "deactivated"}`
      );
      fetchUpsaleDiscounts();
    } catch (error) {
      console.error("Error toggling upsale discount:", error);
      toast.error("Failed to update upsale discount");
    }
  };

  return (
    <AdminLayout>
      <div className="p-8">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-4xl font-bold text-gray-800 mb-2 flex items-center gap-2">
              <TrendingUp className="w-10 h-10 text-red-600" />
              Upsale Discounts
            </h1>
            <p className="text-gray-600">
              Create smart upsale combinations to boost sales
            </p>
          </div>
          <Button
            onClick={handleAddDiscount}
            className="bg-red-600 hover:bg-red-700"
          >
            <Plus size={16} className="mr-2" />
            Add Upsale Discount
          </Button>
          <Button
            onClick={() => {
              setCreditForm({
                min_value: "",
                max_value: "",
                credit_percent: "",
              });
              setEditingSlab(null);
              setCreditModalOpen(true);
            }}
            className="bg-blue-600 hover:bg-blue-700"
          >
            <Plus size={16} className="mr-2" />
            Add Credit Slab
          </Button>
        </div>

        <div className="grid gap-4">
          {upsaleDiscounts.length === 0 ? (
            <Card>
              <CardContent className="p-8 text-center">
                <TrendingUp className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">
                  No upsale discounts yet. Create one to start boosting your
                  sales!
                </p>
              </CardContent>
            </Card>
          ) : (
            upsaleDiscounts.map((discount) => (
              <Card
                key={discount.id}
                className={`${!discount.is_active ? "opacity-60" : ""}`}
              >
                <CardHeader>
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <CardTitle className="text-xl flex items-center gap-2">
                        {discount.name}
                        {discount.is_active ? (
                          <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full">
                            Active
                          </span>
                        ) : (
                          <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full">
                            Inactive
                          </span>
                        )}
                      </CardTitle>
                      <p className="text-sm text-gray-600 mt-1">
                        {discount.description}
                      </p>
                      <div className="mt-2 text-2xl font-bold text-red-600">
                        {discount.discount_value}% OFF
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => toggleActive(discount)}
                      >
                        {discount.is_active ? "Deactivate" : "Activate"}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEditDiscount(discount)}
                      >
                        <Edit size={16} />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(discount.id)}
                      >
                        <Trash2 size={16} className="text-red-600" />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-sm font-semibold text-gray-700 mb-2">
                      Combination Items:
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {discount.combination_items?.map((item, idx) => (
                        <div
                          key={idx}
                          className={`px-3 py-1 rounded-full text-sm ${
                            item.is_upsale_target
                              ? "bg-red-100 text-red-700 font-semibold border-2 border-red-300"
                              : "bg-white text-gray-700 border border-gray-300"
                          }`}
                        >
                          {item.name}
                          {item.is_upsale_target && " 🎯"}
                        </div>
                      ))}
                    </div>
                    <p className="text-xs text-gray-500 mt-2">
                      🎯 = Upsale target (gets the discount)
                    </p>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
        {/* Credit Slabs List */}
        <div className="mt-10">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">
            Credit Slabs
          </h2>

          {creditSlabs.length === 0 ? (
            <Card>
              <CardContent className="p-6 text-center text-gray-500">
                No credit slabs added yet.
              </CardContent>
            </Card>
          ) : (
            creditSlabs.map((slab) => (
              <Card key={slab.id} className="mb-4">
                <CardContent className="p-4 flex justify-between items-center">
                  <div>
                    <p className="text-sm text-gray-700">
                      Min: <b>{slab.min_value}</b> | Max:{" "}
                      <b>{slab.max_value}</b>
                    </p>
                    <p className="text-lg font-semibold text-blue-700">
                      {slab.credit_percent}% Credit
                    </p>
                  </div>

                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleEditSlab(slab)}
                    >
                      <Edit size={16} />
                    </Button>

                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleDeleteSlab(slab.id)}
                    >
                      <Trash2 size={16} className="text-red-600" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>

        {/* Add/Edit Modal */}
        <Dialog open={modalOpen} onOpenChange={setModalOpen}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editingDiscount ? "Edit" : "Create"} Upsale Discount
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label>Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  placeholder="e.g., Entree + Breads + Curry Combo"
                />
              </div>

              <div>
                <Label>Description *</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) =>
                    setFormData({ ...formData, description: e.target.value })
                  }
                  placeholder="e.g., Add any Entree item and get 10% off on that Entree!"
                  rows={2}
                />
              </div>

              <div>
                <Label>Discount Value (%) *</Label>
                <Input
                  type="number"
                  value={formData.discount_value}
                  onChange={(e) =>
                    setFormData({ ...formData, discount_value: e.target.value })
                  }
                  placeholder="10"
                  min="0"
                  max="100"
                />
              </div>

              <div>
                <Label className="text-base font-semibold">
                  Combination Items *
                </Label>
                <p className="text-sm text-gray-600 mb-3">
                  Add items that must be in the cart. Mark the item that gets
                  the discount as "Upsale Target" 🎯
                </p>

                {formData.combination_items.map((item, index) => (
                  <div
                    key={index}
                    className="border rounded-lg p-3 mb-3 bg-gray-50"
                  >
                    <div className="flex gap-2 items-start">
                      <div className="flex-1 space-y-2">
                        <select
                          value={item.type}
                          onChange={(e) =>
                            updateCombinationItem(index, "type", e.target.value)
                          }
                          className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                        >
                          <option value="">Select Type</option>
                          <option value="category">Category</option>
                          <option value="item">Specific Item</option>
                        </select>

                        {item.type === "category" && (
                          <select
                            value={item.id}
                            onChange={(e) =>
                              updateCombinationItem(index, "id", e.target.value)
                            }
                            className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                          >
                            <option value="">Select Category</option>
                            {categories.map((cat) => (
                              <option key={cat.id} value={cat.id}>
                                {cat.name}
                              </option>
                            ))}
                          </select>
                        )}

                        {item.type === "item" && (
                          <select
                            value={item.id}
                            onChange={(e) =>
                              updateCombinationItem(index, "id", e.target.value)
                            }
                            className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                          >
                            <option value="">Select Item</option>
                            {menuItems.map((menuItem) => (
                              <option key={menuItem.id} value={menuItem.id}>
                                {menuItem.name}
                              </option>
                            ))}
                          </select>
                        )}

                        <label className="flex items-center gap-2 text-sm">
                          <input
                            type="checkbox"
                            checked={item.is_upsale_target}
                            onChange={(e) =>
                              updateCombinationItem(
                                index,
                                "is_upsale_target",
                                e.target.checked
                              )
                            }
                            className="rounded"
                          />
                          <span className="font-semibold">
                            🎯 This is the upsale target (gets discount)
                          </span>
                        </label>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeCombinationItem(index)}
                        className="text-red-600"
                      >
                        <X size={16} />
                      </Button>
                    </div>
                  </div>
                ))}

                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => addCombinationItem("category")}
                  >
                    <Plus size={14} className="mr-1" />
                    Add Category
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => addCombinationItem("item")}
                  >
                    <Plus size={14} className="mr-1" />
                    Add Specific Item
                  </Button>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is_active"
                  checked={formData.is_active}
                  onChange={(e) =>
                    setFormData({ ...formData, is_active: e.target.checked })
                  }
                  className="rounded"
                />
                <Label htmlFor="is_active">Active</Label>
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <Button variant="outline" onClick={() => setModalOpen(false)}>
                  Cancel
                </Button>
                <Button
                  onClick={handleSave}
                  className="bg-red-600 hover:bg-red-700"
                >
                  {editingDiscount ? "Update" : "Create"}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
        {/* Credit Slab Modal */}
        <Dialog open={creditModalOpen} onOpenChange={setCreditModalOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>
                {editingSlab ? "Edit Credit Slab" : "Add Credit Slab"}
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-4">
              <div>
                <Label>Min Value *</Label>
                <Input
                  type="number"
                  value={creditForm.min_value}
                  onChange={(e) =>
                    setCreditForm({ ...creditForm, min_value: e.target.value })
                  }
                  placeholder="0"
                />
              </div>

              <div>
                <Label>Max Value *</Label>
                <Input
                  type="number"
                  value={creditForm.max_value}
                  onChange={(e) =>
                    setCreditForm({ ...creditForm, max_value: e.target.value })
                  }
                  placeholder="50"
                />
              </div>

              <div>
                <Label>Credit Percent (%) *</Label>
                <Input
                  type="number"
                  value={creditForm.credit_percent}
                  onChange={(e) =>
                    setCreditForm({
                      ...creditForm,
                      credit_percent: e.target.value,
                    })
                  }
                  placeholder="10"
                />
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <Button
                  variant="outline"
                  onClick={() => setCreditModalOpen(false)}
                >
                  Cancel
                </Button>
                <Button
                  className="bg-blue-600 hover:bg-blue-700"
                  onClick={handleSaveSlab}
                >
                  {editingSlab ? "Update" : "Create"}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
